<template>
  <table class="table table-responsive dt-responsive dataTable activity-builder-base-table fixed" style="border-collapse: collapse; border-spacing: 0; width: 100%">
    <colgroup v-for="(column, index) in columns" :key="index">
      <col :style="column.colgroupstyle" />
    </colgroup>
    <thead class="activity-builder-base-table__thead">
      <tr class="activity-builder-base-table__thead-tr">
        <th v-for="column in columns" :key="column.name" @click.stop="onSortColumn(column, column.sort)" :class="getHeaderClassesOfColumn(column)" class="activity-builder-base-table__th nowrap" :ref="`activity-base-table-header__${column.name}`">
          <div class="position-relative">
            <div class="d-inline-block">
              {{ column.heading }}
              <template v-if="column.sortable">
                <a class="sortUp">
                  <i class="fa fa-angle-up" aria-hidden="true" @click.stop="onSortColumn(column, 'ASC')"></i>
                </a>
                <a class="sortDown">
                  <i class="fa fa-angle-down" aria-hidden="true" @click.stop="onSortColumn(column, 'DESC')"></i>
                </a>
              </template>
            </div>
            <template v-if="column.hasToolTipInfo && column.toolTipContent">
              <img :id="uniqueColumnIds[column.name]" class="position-absolute activity-builder-base-table__tooltip-image ml-2" src="static/img/surveyPreview/previewtoltip.png" alt tabindex="0" />
              <b-tooltip :target="uniqueColumnIds[column.name]" placement="top" triggers="click blur" :delay="{ show: 250, hide: 5000 }">{{ column.toolTipContent }}</b-tooltip>
            </template>
          </div>
        </th>
      </tr>
    </thead>

    <tbody class="activity-builder-base-table__tbody" v-if="columns && columns.length">
      <tr :class="previewSelectedId && row.id === previewSelectedId ? 'preview-background-grey' : ''" v-for="(row, index) in data" :key="row.id || index" class="activity-builder-base-table__tbody-tr">
        <td v-for="column in columns" :key="column.name" class="activity-builder-base-table__td" :style="getStyleObjForTd(column)">
          <template v-if="column.isRenderCustomComponent || column.transformContent">
            <span class="d-inline-block" :class="getContentClassesOfColumn(column)">
              <slot :name="column.name" :data="{ row, column }"></slot>
            </span>
          </template>

          <template v-else>
            <template v-if="column.hasRouterLink">
              <router-link
                v-if="viewMode == false && tableType != 'Published' && tableType != 'Active'"
                :id="row.id + '-' + column.name"
                :to="{
                  name: column.routerName,
                  params: getRouterParams(column, row)
                }"
                class="d-inline-block"
                :class="getContentClassesOfColumn(column)"
                :style="getContentStyleObject(column)"
                >{{ row[column.name] }}</router-link
              >
              <span v-else class="d-inline-block regular-name truncate" :id="row.id + '-' + column.name" :style="getContentStyleObject(column)">{{ row[column.name] }}</span>
            </template>

            <template v-else>
              <span v-if="column.name == 'createdBy' && migrationData && migrationData.threadLibrary && row['targetLibrary'] == 'Thread'" :id="row.id + '-' + column.name" class="d-inline-block" :class="getContentClassesOfColumn(column)" :style="getContentStyleObject(column)">THREAD</span>
              <span v-else :id="row.id + '-' + column.name" class="d-inline-block" :class="getContentClassesOfColumn(column)" :style="getContentStyleObject(column)">{{ row[column.name] }}</span>
            </template>
            <template v-if="column.showTooltip">
              <b-tooltip v-if="row[column.name] && row[column.name].length > column.tooltipLength" :target="row.id + '-' + column.name" placement="top" triggers="hover blur" :delay="{ show: 100, hide: 500 }">{{ row[column.name] }}</b-tooltip>
            </template>
          </template>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
export default {
  name: "base-table",
  props: {
    data: {
      type: Array,
      required: true
    },
    columnNames: {
      type: Array,
      required: true
    },
    useColumnMetaData: {
      type: Boolean,
      default: true,
      required: false
    },
    columnMetaData: {
      type: Object
    },
    previewSelectedId: null,
    viewMode: {
      type: Boolean,
      default: false
    },
    tableType: {
      type: String,
      default: ""
    },
    migrationData: { type: Object }
  },
  data() {
    return {
      ASCENDING: 0,
      DESCENDING: -1,
      columnSortInfo: {},
      uniqueColumnIds: {},
      currentTimeStamp: Date.now()
    };
  },
  computed: {
    columns() {
      if (!this.columnNames) {
        return [];
      }
      if (!this.useColumnMetaData) {
        return this.columnNames.map(columnName => ({
          name: columnName,
          heading: columnName,
          sort: "ASC"
        }));
      }
      const columns = this.columnNames.map(columnName => {
        if (this.migrationData && this.migrationData.threadLibrary && Object.prototype.hasOwnProperty.call(this.columnMetaData, columnName)) {
          return this.columnMetaData[columnName];
        } else if (columnName != "Version" && columnName != "Instances" && Object.prototype.hasOwnProperty.call(this.columnMetaData, columnName)) {
          return this.columnMetaData[columnName];
        } else {
          return {};
        }
      });
      return columns;
    }
  },

  created() {
    this.setupColumnIds();
  },

  mounted() {
    this.setupColumnSortInfo();
  },

  methods: {
    getStyleObjForTd(column) {
      const styleObj = {};
      if (column.fixedWidth) {
        styleObj.width = column.fixedWidth;
      } else {
        if (column.maxWidth) {
          styleObj["max-width"] = column.maxWidth;
        }
        if (column.minWidth) {
          styleObj["min-width"] = column.minWidth;
        }
      }
      styleObj["text-align"] = column.textAlign;
      return styleObj;
    },
    getcolgroupStyleObj(column) {
      return column.colgroupstyle;
    },
    setupColumnIds() {
      this.columnNames.forEach(column => (this.uniqueColumnIds[column] = `activity-header-${column}-${this.currentTimeStamp}`));
    },
    getContentStyleObject(column) {
      const styleObj = {};
      if (column.truncateContent && column.truncateLineNumber) {
        styleObj["-webkit-line-clamp"] = column.truncateLineNumber;
      }
      if (column.applyWidthOnContent) {
        styleObj.width = column.fixedWidth;
      }
      return styleObj;
    },
    getClassesFromArray(classesArray) {
      if (!classesArray) {
        return [];
      }
      if (!classesArray.length) {
        return [];
      }
      return classesArray.map(cssClass => ({ [cssClass]: true }));
    },
    onSortColumn(column, order) {
      if (column.sortable) {
        if (order == "ASC") {
          column.sort = "DESC";
        } else {
          column.sort = "ASC";
        }
        this.$emit("onSortColumn", {
          column,
          order
        });
      }
    },
    setupColumnSortInfo() {
      this.columnNames.forEach(column => {
        this.columnSortInfo[column] = 1;
      });
    },
    getHeaderClassesOfColumn(column) {
      let classesArray = [];
      if (column.headerClasses) {
        classesArray = this.getClassesFromArray(column.headerClasses);
      }
      if (column.sortable) {
        classesArray.push({ sorting: true });
        classesArray.push("cursor-pointer");
      }
      return classesArray;
    },
    getContentClassesOfColumn(column) {
      let classesArray = [];
      if (column.contentClasses && column.contentClasses instanceof Array && column.contentClasses.length) {
        classesArray = this.getClassesFromArray(column.contentClasses);
      }
      if (column.truncateContent) {
        classesArray.push({ truncate: true });
      }
      return classesArray;
    },
    getContentWrapperStyleObject(column) {
      const styleObj = {};
      if (column.maxWidth) {
        styleObj["max-width"] = column.maxWidth;
      }
      if (column.truncateContent && column.truncateLineNumber) {
        styleObj["-webkit-line-clamp"] = column.truncateLineNumber;
      }
      return styleObj;
    },
    getToolTipTarget(column) {
      const ref = `activity-table-header-tooltip__${column.name}`;
      return this.$refs[ref];
    },
    getRouterParams(columnMetaData, row) {
      if (!columnMetaData.routerParams) {
        return;
      }
      const allParams = Object.keys(columnMetaData.routerParams);

      const routerParams = {};

      allParams.forEach(param => {
        const field = columnMetaData.routerParams[param];
        routerParams[param] = row[field];
      });

      return routerParams;
    }
  }
};
</script>

<style scoped lang="scss">
@mixin truncate-content {
  display: -webkit-box !important;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100%);
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

table {
  display: table !important;
}

.activity-builder-base-table {
  width: 100%;

  .text-align-center {
    text-align: center;
  }

  &__container {
    width: 100%;
    margin: 0 auto;
  }

  &__tooltip-image {
    outline: none;
  }
  .nowrap {
    white-space: nowrap;
  }

  .text-align-right {
    text-align: right;
  }
}

table.activity-builder-base-table th {
  font-size: 11px;
  color: #b0bac9;
  border-bottom: 1px solid #dee2e6;
  border-top: none;
  padding: 5px;
}

table.activity-builder-base-table td {
  font-size: 12px;
  padding: 5px;
  color: #000;
  border-top: none;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

.table th,
.table td {
  vertical-align: top;
}

.name {
  font-size: 14px;
  color: #4c8ce4;
  font-weight: bold;
  padding-top: 5px;
  min-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.text-align-right {
  text-align: right;
}

.type {
  font-weight: bold;
}

table.dataTable td.languages-box {
  padding: 5px 10px;
}
.sortUp {
  position: absolute;
  margin-top: -3px;
  padding-left: 4px;
  width: 0;
  height: 0;
  background: transparent;
  border-top-width: 0;
  cursor: pointer;
}
.sortDown {
  position: absolute;
  padding-left: 4px;
  width: 0;
  height: 0;
  cursor: pointer;
  top: 3px;
  z-index: 1;
}
.preview-background-grey {
  background-color: rgba(211, 223, 228, 0.6);
}
.table.fixed {
  table-layout: fixed;
  word-break: break-all;
}
.table.fixed td {
  overflow: hidden;
}
.cursor-pointer {
  cursor: pointer;
}
.regular-name {
  font-size: 14px;
  color: #000000;
  font-weight: bold;
  padding-top: 5px;
  min-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
